import { createStore } from 'vuex';
import { notify } from "@kyvg/vue3-notification";

const dedupeGames = (games) => {
    const seen = new Set();
    const deduped = [];

    for (const game of Array.isArray(games) ? games : []) {
        if (!game) {
            continue;
        }
        const key = game.id != null
            ? `id:${game.id}`
            : `name:${String(game.name || '').trim().toLowerCase()}|store:${String(game.store || '').trim().toLowerCase()}|app:${String(game.app_id ?? '')}`;
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        deduped.push(game);
    }

    return deduped;
};

const apiCallWithAuth = async (url, options = {}) => {
    try {
        const token = localStorage.getItem('access_token');

        const response = await fetch(url, {
            ...options,
            credentials: 'include',
            headers: {
                ...options.headers,
                ...(token && { 'Authorization': `Bearer ${token}` })
            }
        });

        if (!response.ok) {
            if (response.status === 401) {
                const newToken = await store.dispatch('refreshToken');
                if (newToken) {
                    const newOptions = {
                        ...options,
                        headers: {
                            ...options.headers,
                            'Authorization': `Bearer ${newToken}`
                        }
                    };
                    return await fetch(url, {
                        ...newOptions,
                        credentials: 'include'
                    });
                } else {
                    store.dispatch('logout');
                    throw new Error('Authentication failed');
                }
            }
            const errorData = await response.json();
            const error = new Error(errorData.detail || `HTTP ${response.status}`);
            error.response = response;
            error.errorData = errorData;
            throw error;
        }

        return response;
    } catch (error) {
        console.error('apiCallWithAuth error:', error);
        throw error;
    }
};

export const store = createStore({
    state: {
        userInput: '',
        lastSearchQuery: '', // Store the query effectively used for current results
        filters: { genres: [], tags: [], categories: [], concepts: [] }, // Dynamic filters
        games: [],
        isLoading: false,
        isLoadingMore: false,
        errorMessage: null,
        nextCursor: null,
        hasMore: false,
        searchId: null,
        warningMessage: null,
        warningLevel: null,
        fallbackApplied: false,
        relevantCount: 0,
        isLoggedIn: false,
        user: null,
        steamProfile: null,
        steamName: '',
        steamAvatar: '',
        isInitialized: false,
        isInitializing: false,
        totalGameCount: 50000, // Default fallback
        seoPage: null,
        seoPageLoading: false,
        seoPageError: null,
        seoPages: [],
        seoPagesLoading: false,
        seoPagesError: null,
    },
    mutations: {
        setUserInput(state, input) {
            state.userInput = input;
        },
        setFilters(state, filters) {
            state.filters = filters;
        },
        setLastSearchQuery(state, query) {
            state.lastSearchQuery = query;
        },
        appendToUserInput(state, text) {
            const trimmedInput = state.userInput.trim();
            const newText = text.trim();

            if (trimmedInput.toLowerCase().includes(newText.toLowerCase())) {
                return;
            }

            if (trimmedInput === '') {
                state.userInput = newText;
            } else {
                state.userInput = `${trimmedInput} ${newText}`;
            }
        },
        setGames(state, games) {
            state.games = dedupeGames(games);
        },
        appendGames(state, newGames) {
            if (Array.isArray(newGames)) {
                state.games = dedupeGames([...state.games, ...newGames]);
            }
        },
        setLoading(state, status) {
            state.isLoading = status;
        },
        setLoadingMore(state, status) {
            state.isLoadingMore = status;
        },
        setErrorMessage(state, message) {
            state.errorMessage = message;
        },
        setPagination(state, { nextCursor, hasMore }) {
            state.nextCursor = nextCursor;
            state.hasMore = hasMore;
        },
        setSearchId(state, id) {
            state.searchId = id;
        },
        setResultMeta(state, { warningMessage, warningLevel, fallbackApplied, relevantCount }) {
            state.warningMessage = warningMessage || null;
            state.warningLevel = warningLevel || null;
            state.fallbackApplied = !!fallbackApplied;
            state.relevantCount = Number.isFinite(relevantCount) ? relevantCount : 0;
        },
        setIsLoggedIn(state, status) {
            state.isLoggedIn = status;
        },
        setUser(state, user) {
            state.user = user;
            if (user) {
                localStorage.setItem('user', JSON.stringify(user));
            } else {
                localStorage.removeItem('user');
            }
        },
        setSteamProfile(state, profile) {
            state.steamProfile = profile;
        },
        setSteamName(state, name) {
            state.steamName = name;
        },
        setSteamAvatar(state, avatar) {
            state.steamAvatar = avatar;
        },
        clearSteamProfileData(state) {
            state.steamProfile = null;
            state.steamName = '';
            state.steamAvatar = '';
        },
        setInitializing(state, status) {
            state.isInitializing = status;
        },
        setInitialized(state, status) {
            state.isInitialized = status;
        },
        setTotalGameCount(state, count) {
            state.totalGameCount = count;
        },
        setSeoPage(state, page) {
            state.seoPage = page;
        },
        setSeoPageLoading(state, status) {
            state.seoPageLoading = status;
        },
        setSeoPageError(state, error) {
            state.seoPageError = error;
        },
        clearSeoPage(state) {
            state.seoPage = null;
            state.seoPageError = null;
        },
        setSeoPages(state, pages) {
            state.seoPages = Array.isArray(pages) ? pages : [];
        },
        setSeoPagesLoading(state, status) {
            state.seoPagesLoading = status;
        },
        setSeoPagesError(state, error) {
            state.seoPagesError = error;
        },
    },
    actions: {
        async initialize({ commit, state, dispatch }) {
            if (state.isInitialized || state.isInitializing) {
                return;
            }

            // Fetch filters in background
            dispatch('fetchFilters');
            dispatch('fetchGameCount');

            commit('setInitializing', true);

            try {
                const token = localStorage.getItem('access_token');

                if (token) {
                    const newToken = await dispatch('refreshToken');

                    if (newToken) {
                        commit('setIsLoggedIn', true);

                        if (state.user?.steam_id) {
                            try {
                                await dispatch('fetchSteamProfile', state.user.steam_id);
                            } catch (error) {
                                console.error('Failed to load Steam profile on initialization:', error);
                            }
                        }
                    } else {
                        localStorage.removeItem('access_token');
                        localStorage.removeItem('user');
                        commit('setIsLoggedIn', false);
                        commit('setUser', null);
                    }
                } else {
                    commit('setIsLoggedIn', false);
                    commit('setUser', null);
                    localStorage.removeItem('user');
                }
            } catch (error) {
                console.error('Initialization error:', error);
                localStorage.removeItem('access_token');
                localStorage.removeItem('user');
                commit('setIsLoggedIn', false);
                commit('setUser', null);
            } finally {
                commit('setInitialized', true);
                commit('setInitializing', false);
            }
        },

        async searchGames({ commit, state }, options = {}) {
            let useSteamFilter = false;
            let includeAnime = false;
            let includeAdult = false;
            let includeVr = false;
            let random = false;

            if (typeof options === 'boolean') {
                useSteamFilter = options;
            } else if (typeof options === 'object') {
                useSteamFilter = options.useSteam || false;
                includeAnime = options.includeAnime !== undefined ? options.includeAnime : true;
                includeAdult = options.includeAdult !== undefined ? options.includeAdult : false;
                includeVr = options.includeVr !== undefined ? options.includeVr : false;
                random = options.random || false;
            }

            commit('setLoading', true);
            commit('setErrorMessage', null);

            commit('setGames', []);
            commit('setPagination', { nextCursor: null, hasMore: false });
            commit('setSearchId', null);
            commit('setResultMeta', {
                warningMessage: null,
                warningLevel: null,
                fallbackApplied: false,
                relevantCount: 0,
            });
            commit('setLastSearchQuery', state.userInput); // Snapshot the query

            try {
                const url = new URL("/api/v1/games", window.location.origin);
                url.searchParams.append('limit', 20);

                url.searchParams.append('include_anime', includeAnime);
                url.searchParams.append('include_adult', includeAdult);
                url.searchParams.append('include_vr', includeVr);

                if (random) {
                    url.searchParams.append('random', 'true');
                }

                if (useSteamFilter && state.steamProfile) {
                    url.searchParams.append('steam_id', state.steamProfile.steam_id);
                }

                const response = await fetch(url, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json",
                    },
                    body: JSON.stringify({ query: state.userInput }),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    notify({
                        title: 'Search failed',
                        text: errorData.detail,
                        type: 'error',
                    });
                    throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                commit('setGames', data.games);
                commit('setPagination', {
                    nextCursor: data.next_cursor,
                    hasMore: data.has_more,
                });
                commit('setSearchId', data.search_id);
                commit('setResultMeta', {
                    warningMessage: data.warning_message,
                    warningLevel: data.warning_level,
                    fallbackApplied: data.fallback_applied,
                    relevantCount: data.relevant_count,
                });
            } catch (error) {
                commit('setErrorMessage', error.message || "An unknown error occurred");
            } finally {
                commit('setLoading', false);
            }
        },

        async loadMoreGames({ commit, state }) {
            if (state.isLoadingMore || !state.hasMore) {
                return;
            }

            commit('setLoadingMore', true);
            commit('setErrorMessage', null);

            try {
                const url = new URL("/api/v1/games", window.location.origin);
                url.searchParams.append('limit', 20);

                if (state.nextCursor !== null) {
                    url.searchParams.append('cursor', state.nextCursor);
                }

                if (state.searchId !== null) {
                    url.searchParams.append('search_id', state.searchId);
                }

                const response = await fetch(url, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json",
                    },
                    body: JSON.stringify({ query: state.lastSearchQuery }), // Use the snapshot, not live input
                });

                if (!response.ok) {
                    // Check for session expiry (404) or Server Error (500)
                    if (response.status === 404 || response.status === 500) {
                        commit('setPagination', { nextCursor: null, hasMore: false });
                        notify({
                            title: 'Session Ended',
                            text: 'Search session expired or server error. Please search again.',
                            type: 'warn',
                        });
                        return; // Stop execution
                    }

                    const errorData = await response.json();
                    notify({
                        title: 'Could not load more games',
                        text: errorData.detail,
                        type: 'error',
                    });
                    throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                commit('appendGames', data.games);
                commit('setPagination', {
                    nextCursor: data.next_cursor,
                    hasMore: data.has_more,
                });

                commit('setSearchId', data.search_id);
                commit('setResultMeta', {
                    warningMessage: data.warning_message,
                    warningLevel: data.warning_level,
                    fallbackApplied: data.fallback_applied,
                    relevantCount: data.relevant_count,
                });

            } catch (error) {
                commit('setErrorMessage', error.message || "An unknown error occurred");
                // Stop trying to load more if we hit an error to prevent infinite loops
                commit('setPagination', { nextCursor: null, hasMore: false });
            } finally {
                commit('setLoadingMore', false);
            }
        },

        async logGameClick({ state }, clickedGameId) {
            if (!state.searchId) {
                return;
            }

            try {
                const url = new URL("/api/v1/log", window.location.origin);
                await fetch(url, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        search_id: state.searchId,
                        clicked_game_id: clickedGameId,
                    }),
                });
            } catch (error) {
                // ignored
            }
        },

        async logFeedback({ state }, { gameId, feedback }) {
            try {
                // If search_id is null (e.g. from User Page), we can send 0 or omit it if backend handles it
                // But backend likely expects it. We'll send search_id if available, but for user page removals,
                // we might need a dedicated removal endpoint or allow null.
                // Assuming backend handles feedback update by user_id + game_id mainly.

                const payload = {
                    game_id: gameId,
                    feedback: feedback,
                };

                if (state.searchId) {
                    payload.search_id = state.searchId;
                }

                const response = await apiCallWithAuth('/api/v1/feedback', {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload)
                });

                await response.json();

                notify({
                    title: 'Success',
                    text: feedback === 'none' ? 'Feedback removed' : 'Thank you for your feedback!',
                    duration: 2000
                });
            } catch (error) {
                console.error('Feedback error:', error);
                notify({
                    title: 'Error',
                    text: error.message,
                    type: 'error'
                });
            }
        },

        async removeFeedback({ dispatch }, gameId) {
            // Helper specifically for removing feedback (setting to none)
            await dispatch('logFeedback', { gameId, feedback: 'none' });
        },

        async login({ commit, dispatch }, { email, password }) {
            commit('setLoading', true);
            commit('setErrorMessage', null);

            commit('clearSteamProfileData');

            commit('setUser', null);
            commit('setIsLoggedIn', false);
            localStorage.removeItem('access_token');

            try {
                const response = await fetch('/api/v1/login', {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json",
                    },
                    body: JSON.stringify({
                        email: email,
                        password: password,
                    }),
                    credentials: 'include'
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    notify({
                        title: 'Login failed',
                        text: errorData.detail,
                        type: 'error',
                    });
                    throw new Error(errorData.detail || `Login failed! status: ${response.status}`);
                }

                const data = await response.json();

                if (data.access_token) {
                    localStorage.setItem('access_token', data.access_token);
                    if (data.user) {
                        commit('setIsLoggedIn', true);
                        commit('setUser', data.user);

                        if (data.user.steam_id) {
                            try {
                                await dispatch('fetchSteamProfile', data.user.steam_id);
                            } catch (error) {
                                console.error('Failed to fetch Steam profile:', error);
                            }
                        }
                    }
                }

                notify({
                    title: 'Login successful',
                    text: 'Welcome back!',
                });

                return data;
            } catch (error) {
                commit('setErrorMessage', error.message || "Login failed");
                throw error;
            } finally {
                commit('setLoading', false);
            }
        },

        async refreshToken({ commit, dispatch, state }) {
            try {
                const response = await fetch('/api/v1/refresh', {
                    method: "POST",
                    credentials: 'include'
                });

                if (!response.ok) {
                    return null;
                }

                const data = await response.json();

                if (data.access_token) {
                    localStorage.setItem('access_token', data.access_token);

                    if (data.user) {
                        commit('setUser', data.user);
                        commit('setIsLoggedIn', true);

                        if (data.user.steam_id && !state.steamProfile) {
                            try {
                                await dispatch('fetchSteamProfile', data.user.steam_id);
                            } catch (error) {
                                console.error('Failed to fetch Steam profile:', error);
                            }
                        }
                    } else {
                        console.warn('No user data in refresh response');
                    }

                    return data.access_token;
                }

                console.warn('No access token in refresh response');
                return null;
            } catch (error) {
                console.error('Token refresh error:', error);
                return null;
            }
        },

        async signup({ commit }, { email, password, confirm_password }) {
            commit('setLoading', true);
            commit('setErrorMessage', null);

            try {
                const response = await fetch('/api/v1/signup', {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json",
                    },
                    body: JSON.stringify({
                        email: email,
                        password: password,
                        confirm_password: confirm_password,
                    }),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    notify({
                        title: 'Sign up failed',
                        text: errorData.detail,
                        type: 'error',
                    });
                    throw new Error(errorData.detail || `Signup failed! status: ${response.status}`);
                }

                const data = await response.json();

                if (data.access_token) {
                    localStorage.setItem('access_token', data.access_token);
                    if (data.user) {
                        commit('setIsLoggedIn', true);
                        commit('setUser', data.user);
                    }
                }

                notify({
                    title: 'Sign up successful',
                    text: 'Please check your email to verify your account',
                });

                return data;
            } catch (error) {
                commit('setErrorMessage', error.message || "Signup failed");
                throw error;
            } finally {
                commit('setLoading', false);
            }
        },

        async verifyEmail({ commit }, token) {
            try {
                const response = await fetch('/api/v1/verify_email', {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json",
                    },
                    body: JSON.stringify({
                        token: token
                    }),
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.detail || 'Verification failed');
                }

                const data = await response.json();

                if (data.user) {
                    commit('setUser', data.user);
                    commit('setIsLoggedIn', true);
                }

                return data;
            } catch (error) {
                console.error('Email verification error:', error);
                throw error;
            }
        },

        async apiCallWithAuth(_, { url, ...options }) {
            return await apiCallWithAuth(url, options);
        },
        async logout({ commit }) {
            localStorage.removeItem('access_token');
            localStorage.removeItem('user');

            commit('setIsLoggedIn', false);
            commit('setUser', null);
            commit('setGames', []);
            commit('setPagination', { nextCursor: null, hasMore: false });
            commit('clearSteamProfileData');

            try {
                await fetch('/api/v1/logout', {
                    method: 'POST',
                    credentials: 'include'
                });
            } catch (error) {
                console.error('Backend logout error:', error);
            }
        },

        async fetchSteamProfile({ commit }, steamId) {
            try {
                const token = localStorage.getItem('access_token');

                const headers = {};
                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }

                const response = await fetch(`/api/v1/steam/profile/${steamId}`, {
                    headers
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.detail || 'Profile not found');
                }

                const profileData = await response.json();
                commit('setSteamProfile', profileData); // Store full object
                commit('setSteamName', profileData.username);
                commit('setSteamAvatar', profileData.avatar);

                const user = JSON.parse(localStorage.getItem('user'));
                if (user) {
                    user.steam_id = steamId;
                    commit('setUser', user);
                }

                return profileData;
            } catch (error) {
                throw new Error(error.message || 'Failed to fetch Steam profile');
            }
        },

        async unlinkSteamProfile({ commit }) {
            try {
                const response = await apiCallWithAuth('/api/v1/steam/profile', {
                    method: 'DELETE'
                });

                if (!response.ok) {
                    throw new Error('Failed to unlink Steam profile');
                }

                commit('clearSteamProfileData');

                const user = JSON.parse(localStorage.getItem('user'));
                if (user) {
                    delete user.steam_id;
                    commit('setUser', user);
                }

                notify({
                    title: 'Success',
                    text: 'Steam profile unlinked successfully',
                });
            } catch (error) {
                notify({
                    title: 'Error',
                    text: error.message,
                    type: 'error'
                });
                throw error;
            }
        },

        async submitSurvey({ commit }, payload) {
            try {
                const response = await apiCallWithAuth('/api/v1/users/me/survey', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) {
                    throw new Error('Failed to submit survey');
                }

                const data = await response.json();

                // If it updates user preferences/data, commit it
                if (data.user) {
                    commit('setUser', data.user);
                }

                notify({
                    title: 'Thank you!',
                    text: 'Your feedback helps us improve.',
                    type: 'success'
                });
                return data;
            } catch (error) {
                notify({
                    title: 'Submission Failed',
                    text: error.message,
                    type: 'error'
                });
                throw error;
            }
        },

        async fetchFilters({ commit }) {
            try {
                const response = await fetch('/api/v1/games/filters');
                if (response.ok) {
                    const data = await response.json();
                    commit('setFilters', data);
                }
            } catch (error) {
                console.error('Failed to fetch filters:', error);
            }
        },

        async fetchGameCount({ commit }) {
            try {
                const response = await fetch('/api/v1/games/count');
                if (response.ok) {
                    const data = await response.json();
                    commit('setTotalGameCount', data.count);
                }
            } catch (error) {
                console.error('Failed to fetch game count:', error);
            }
        },

        async fetchAutocomplete(_, query) {
            try {
                // Return empty if query is too short
                if (!query || query.length < 2) return [];

                const url = new URL("/api/v1/games/autocomplete", window.location.origin);
                url.searchParams.append('query', query);

                const response = await fetch(url);
                if (response.ok) {
                    return await response.json();
                }
                return [];
            } catch (error) {
                console.error('Autocomplete error:', error);
                return [];
            }
        },

        async analyzeQuery(_, query) {
            try {
                // Allow empty query for random discovery suggestions
                const url = new URL("/api/v1/games/analyze", window.location.origin);
                if (query) {
                    url.searchParams.append('query', query);
                }

                const response = await fetch(url);
                if (response.ok) {
                    return await response.json();
                }
                return { tokens: [], suggestions: [] };
            } catch (error) {
                console.error('Query analysis error:', error);
                return { tokens: [], suggestions: [] };
            }
        },

        async fetchSeoPage({ commit }, slug) {
            commit('setSeoPageLoading', true);
            commit('setSeoPageError', null);

            try {
                const response = await fetch(`/api/v1/seo-pages/${slug}`, {
                    headers: {
                        "Accept": "application/json",
                    }
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.detail || 'SEO page not found');
                }

                const data = await response.json();
                commit('setSeoPage', data);
                return data;
            } catch (error) {
                commit('setSeoPageError', error.message || 'Failed to load SEO page');
                commit('setSeoPage', null);
                throw error;
            } finally {
                commit('setSeoPageLoading', false);
            }
        },

        async loadSeoPageResults({ commit }, slug) {
            commit('setLoading', true);
            commit('setErrorMessage', null);
            commit('setGames', []);
            commit('setPagination', { nextCursor: null, hasMore: false });
            commit('setSearchId', null);
            commit('setResultMeta', {
                warningMessage: null,
                warningLevel: null,
                fallbackApplied: false,
                relevantCount: 0,
            });

            try {
                const response = await fetch(`/api/v1/seo-pages/${slug}/results`, {
                    headers: {
                        "Accept": "application/json",
                    }
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.detail || 'Failed to load SEO page results');
                }

                const data = await response.json();

                commit('setGames', data.games || []);
                commit('setPagination', {
                    nextCursor: null,
                    hasMore: false,
                });
                commit('setSearchId', null);
                commit('setResultMeta', {
                    warningMessage: data.warning_message,
                    warningLevel: data.warning_level,
                    fallbackApplied: data.fallback_applied,
                    relevantCount: data.relevant_count,
                });
                commit('setLastSearchQuery', data.query || '');
            } catch (error) {
                commit('setErrorMessage', error.message || 'Failed to load SEO page results');
            } finally {
                commit('setLoading', false);
            }
        },

        async fetchSeoPagesList({ commit }) {
            commit('setSeoPagesLoading', true);
            commit('setSeoPagesError', null);

            try {
                const response = await fetch('/api/v1/seo-pages', {
                    headers: {
                        Accept: 'application/json',
                    },
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.detail || 'Failed to load SEO pages');
                }

                commit('setSeoPages', Array.isArray(data.pages) ? data.pages : []);
                return data.pages || [];
            } catch (error) {
                commit('setSeoPagesError', error.message || 'Failed to load SEO pages');
                commit('setSeoPages', []);
                throw error;
            } finally {
                commit('setSeoPagesLoading', false);
            }
        },
    },
    getters: {
        getUserInput: (state) => state.userInput,
        getGames: (state) => state.games,
        isLoading: (state) => state.isLoading,
        isLoadingMore: (state) => state.isLoadingMore,
        getErrorMessage: (state) => state.errorMessage,
        canLoadMore: (state) => !state.isLoadingMore && state.hasMore,
        token: () => localStorage.getItem('access_token'),
        isLoggedIn: (state) => state.isLoggedIn,
        getSteamProfile: (state) => state.steamProfile,
        getSteamName: (state) => state.steamName,
        getSteamAvatar: (state) => state.steamAvatar,
        hasSteamLinked: (state) => !!state.user?.steam_id,
        getUser: (state) => state.user
    }
});
